import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';

function groupBySlot<T extends { startTime: string; endTime: string }>(items: T[]) {
  const groups = new Map<string, T[]>();

  items.forEach((item) => {
    const key = `${item.startTime} – ${item.endTime}`;
    const existing = groups.get(key) ?? [];
    existing.push(item);
    groups.set(key, existing);
  });

  return Array.from(groups.entries()).map(([slotLabel, workshops]) => ({
    slotLabel,
    workshops,
  }));
}

export function AdminOverviewPrintPage() {
  const navigate = useNavigate();

  const sessionQuery = useQuery({
    queryKey: ['admin-session'],
    queryFn: api.adminSession,
  });

  const workshopsQuery = useQuery({
    queryKey: ['admin-workshops'],
    queryFn: api.adminWorkshops,
    enabled: sessionQuery.isSuccess,
  });

  useEffect(() => {
    if (sessionQuery.error instanceof ApiError && sessionQuery.error.status === 401) {
      navigate('/admin/login');
    }
  }, [navigate, sessionQuery.error]);

  if (!workshopsQuery.data) {
    return <p className="print-loading">Gesamtübersicht wird geladen…</p>;
  }

  const slotGroups = groupBySlot(workshopsQuery.data.workshops);

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <button className="button" type="button" onClick={() => window.print()}>
          Drucken / PDF
        </button>
      </div>

      <article className="print-sheet">
        <header className="print-sheet__header">
          <p>!GROW 2026</p>
          <h1>Gesamtübersicht Workshops</h1>
          <strong>{workshopsQuery.data.eventLabel}</strong>
          <span>{workshopsQuery.data.workshops.length} Workshops gesamt</span>
        </header>

        {slotGroups.map((group) => (
          <section className="print-section" key={group.slotLabel}>
            <div className="print-section__header">
              <h2>{group.slotLabel}</h2>
              <span>{group.workshops.length} Räume / Workshops</span>
            </div>

            <table className="print-table print-table--overview">
              <thead>
                <tr>
                  <th>Raum</th>
                  <th>Workshop</th>
                  <th>Anmeldungen</th>
                  <th>Freie Plätze</th>
                  <th>Teilnehmerinnen</th>
                </tr>
              </thead>
              <tbody>
                {group.workshops.map((workshop) => (
                  <tr key={workshop.id}>
                    <td className="room-label">{workshop.roomName}</td>
                    <td>
                      <strong>{workshop.title}</strong>
                      {workshop.subtitle ? <div className="print-cell-subtitle">{workshop.subtitle}</div> : null}
                    </td>
                    <td>{workshop.bookedCount}</td>
                    <td>{workshop.isBookable ? (workshop.remainingSeats ?? 0) : 'Nicht buchbar'}</td>
                    <td>
                      {workshop.participants.length ? (
                        <div className="print-participant-list">
                          {workshop.participants.map((participant) => (
                            <div key={participant.id}>
                              {participant.displayName} ({participant.code})
                            </div>
                          ))}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </article>
    </div>
  );
}
